import React from 'react';
import { Helmet } from 'react-helmet-async';
import { FaCode, FaChartLine, FaRocket, FaBrain, FaHandPaper, FaMapMarkerAlt, FaGlobe, FaFacebook, FaGoogle, FaWordpress, FaShopify, FaReact, FaSearch } from 'react-icons/fa';
import './About.css';

const About = ({ selectedServices = [] }) => {
  // Determine content based on selected services
  const isDigitalMarketing = selectedServices.includes('digital-marketing');
  const isWebDevelopment = selectedServices.includes('web-development');
  const isBothServices = isDigitalMarketing && isWebDevelopment;

  // Dynamic content configuration
  const getAboutContent = () => {
    if (isBothServices) {
      return {
        leadText: "I'm a full-stack digital architect who builds complete business ecosystems. I don't just create websites or run ads—I engineer integrated solutions that turn your digital presence into a profit-generating machine.",
        mainText: "My unique combination of development skills and marketing expertise means I can build exactly what your business needs, then market it strategically for maximum impact. From custom web applications to viral social campaigns, I deliver end-to-end solutions.",
        highlightText: "technical expertise, marketing psychology, and business strategy",
        philosophy: "I believe in creating digital experiences that work as hard as you do. Every website I build and every campaign I run is designed with one goal: measurable growth that transforms your business.",
        focusItems: [
          { icon: FaCode, text: "Custom Web Development" },
          { icon: FaChartLine, text: "Performance Marketing" },
          { icon: FaRocket, text: "Growth Engineering" },
          { icon: FaBrain, text: "Conversion Optimization" }
        ]
      };
    } else if (isDigitalMarketing) {
      return {
        leadText: "I'm a social media strategist and brand builder who uses data and creativity to create explosive growth. I don't just post content; I architect marketing funnels that turn followers into loyal, paying customers.",
        mainText: "My passion is helping brands find their authentic voice and connect with their audience in ways that drive real business results. I combine proven marketing frameworks with cutting-edge tools to deliver campaigns that actually convert.",
        highlightText: "psychology, data analytics, and creative storytelling",
        philosophy: "I believe great marketing isn't about being everywhere—it's about being exactly where your customers are, with exactly the message they need to hear, at exactly the right moment.",
        focusItems: [
          { icon: FaFacebook, text: "Meta & Facebook Ads" },
          { icon: FaGoogle, text: "Google Ads & PPC" },
          { icon: FaSearch, text: "SEO & Content Strategy" },
          { icon: FaChartLine, text: "Social Media Growth" }
        ]
      };
    } else if (isWebDevelopment) {
      return {
        leadText: "I'm a full-stack developer who builds more than websites—I create digital experiences that convert visitors into customers. Every line of code is written with user experience and business growth in mind.",
        mainText: "My development philosophy centers on creating fast, secure, and scalable solutions that grow with your business. Whether it's a custom web application, e-commerce platform, or corporate website, I ensure it delivers results.",
        highlightText: "clean code, user experience, and performance optimization",
        philosophy: "I believe great websites aren't just visually appealing—they're conversion machines that work 24/7 to grow your business while providing exceptional user experiences.",
        focusItems: [
          { icon: FaReact, text: "React & Frontend Development" },
          { icon: FaCode, text: "Full-Stack Applications" },
          { icon: FaWordpress, text: "WordPress Solutions" },
          { icon: FaShopify, text: "E-commerce Platforms" }
        ]
      };
    }
    // Fallback to original content
    return {
      leadText: "I'm a social media strategist and brand builder who uses technology to create explosive growth. I don't just post content; I build and manage entire marketing ecosystems that turn followers into loyal customers.",
      mainText: "My passion is helping brands find their voice and connect with their audience in a meaningful way. I combine data-driven marketing strategies with the technical ability to build the tools and platforms needed for success.",
      highlightText: "design, tech, and human psychology",
      philosophy: "I believe in building digital experiences that don't just look good, but actually work. Every line of code I write and every campaign I run is designed with one goal: measurable results that grow your business.",
      focusItems: [
        { icon: FaChartLine, text: "Social Media Marketing" },
        { icon: FaBrain, text: "Brand Strategy" },
        { icon: FaRocket, text: "Growth Campaigns" },
        { icon: FaCode, text: "Marketing Tech" }
      ]
    };
  };

  const content = getAboutContent();

  // SEO content based on selected services
  const getSEOContent = () => {
    if (isBothServices) {
      return {
        title: "About Harish Kumar - Full-Stack Digital Architect & Growth Strategist",
        description: "Meet Harish Kumar, a full-stack developer and digital marketing expert who builds complete business ecosystems. Combining technical expertise with marketing psychology for measurable growth.",
        keywords: "full-stack developer, digital marketing expert, web development, growth hacking, business automation, conversion optimization"
      };
    } else if (isDigitalMarketing) {
      return {
        title: "About Harish Kumar - Digital Marketing Expert & Brand Growth Strategist",
        description: "Meet Harish Kumar, a data-driven digital marketing expert who creates viral campaigns and grows brands. Specializing in Meta Ads, Google Ads, and social media marketing.",
        keywords: "digital marketing expert, social media marketing, Meta ads, Google ads, brand growth, viral campaigns, ROI optimization"
      };
    } else if (isWebDevelopment) {
      return {
        title: "About Harish Kumar - Full-Stack Web Developer & Digital Solutions Expert",
        description: "Meet Harish Kumar, a full-stack web developer who builds conversion-focused websites and applications. Specializing in React, PHP, WordPress, and e-commerce platforms.",
        keywords: "full-stack web developer, React developer, PHP developer, WordPress expert, e-commerce development, conversion optimization"
      };
    }
    return {
      title: "About Harish Kumar - Digital Marketing Expert & Web Developer",
      description: "Meet Harish Kumar, a digital marketing expert and web developer who combines technical skills with marketing expertise to deliver comprehensive business solutions.",
      keywords: "digital marketing, web development, social media marketing, technical expertise, business solutions"
    };
  };

  const seoContent = getSEOContent();

  return (
    <>
      <Helmet>
        <title>{seoContent.title}</title>
        <meta name="description" content={seoContent.description} />
        <meta name="keywords" content={seoContent.keywords} />
        <meta property="og:title" content={seoContent.title} />
        <meta property="og:description" content={seoContent.description} />
        <meta property="og:url" content="https://portfolioweb.fliphatmedia.com/#about" />
        <meta name="twitter:title" content={seoContent.title} />
        <meta name="twitter:description" content={seoContent.description} />
        <link rel="canonical" href="https://portfolioweb.fliphatmedia.com/#about" />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AboutPage",
            "mainEntity": {
              "@type": "Person",
              "name": "Harish Kumar",
              "jobTitle": isBothServices ? "Full-Stack Digital Architect" : isDigitalMarketing ? "Digital Marketing Expert" : "Full-Stack Web Developer",
              "description": content.leadText,
              "knowsAbout": content.focusItems.map(item => item.text),
              "address": {
                "@type": "PostalAddress",
                "addressLocality": "Ahmedabad",
                "addressRegion": "Gujarat",
                "addressCountry": "IN"
              },
              "url": "https://portfolioweb.fliphatmedia.com",
              "sameAs": [
                "https://www.linkedin.com/in/connectharish",
                "https://www.portfolioweb.fliphatmedia.com"
              ]
            }
          })}
        </script>
      </Helmet>
      
      <section id="about" className="about-section section" itemScope itemType="https://schema.org/AboutPage">
      <div className="container">
        <h2 className="section-title">
          <FaHandPaper className="section-icon" aria-hidden="true" /> About Me
        </h2>
        
        <img 
          src="/images/harish-kumar-profile.jpg" 
          alt="Harish Kumar - Professional Digital Marketing Expert and Web Developer based in Ahmedabad, India"
          className="about-profile-img"
          itemProp="image"
          loading="lazy"
        />
        
        <div className="about-content" itemProp="mainEntity" itemScope itemType="https://schema.org/Person">
          <div className="about-text">
            <div className="about-intro">
              <p className="lead-text" itemProp="description">
                {content.leadText}
              </p>
              <p itemProp="knowsAbout">
                {content.mainText} 
                This unique blend allows me to deliver comprehensive solutions that drive real business results.
              </p>
              <p>
                My work merges <span className="highlight-text">{content.highlightText}</span> — 
                all powered by cutting-edge tools, {isBothServices ? 'full-stack expertise' : isWebDevelopment ? 'development experience' : 'marketing experience'}, and a strategic mindset 
                that won't stop asking <em>"How can we {isBothServices ? 'build and grow' : isDigitalMarketing ? 'convert' : 'optimize'} better?"</em>
              </p>
            </div>
            
            <div className="about-details">
              <div className="philosophy">
                <h3>My Philosophy</h3>
                <p itemProp="makesOffer">
                  {content.philosophy}
                </p>
              </div>
            </div>
          </div>
          
          <div className="about-visual">
            <div className="what-i-do">
              <h4>{isBothServices ? 'Full-Stack Capabilities:' : isDigitalMarketing ? 'Marketing Expertise:' : isWebDevelopment ? 'Development Skills:' : 'My Core Focus:'}</h4>
              <div className="do-list" role="list" aria-label="Core skills and expertise">
                {content.focusItems.map((item, index) => (
                  <span key={index} className="do-item" role="listitem" itemProp="hasOccupation">
                    <item.icon aria-hidden="true" /> {item.text}
                  </span>
                ))}
              </div>
            </div>
            
            <div className="location-info" itemScope itemType="https://schema.org/Place">
              <div className="location-card">
                <h4>
                  <FaMapMarkerAlt className="location-icon" aria-hidden="true" /> 
                  <span itemProp="address" itemScope itemType="https://schema.org/PostalAddress">
                    Based in <span itemProp="addressLocality">Ahmedabad</span>, <span itemProp="addressCountry">India</span>
                  </span>
                </h4>
                <p>Available for remote work worldwide</p>
                <div className="contact-links">
                  <a 
                    href="https://www.portfolioweb.fliphatmedia.com" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="contact-link"
                    aria-label="Visit Harish Kumar's main website"
                    itemProp="url"
                  >
                    <FaGlobe className="website-icon" aria-hidden="true" /> portfolioweb.fliphatmedia.com
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
    </>
  );
};

export default About;
